// <countdown-timer> — Soft Power pre-order countdown.
//
// Attributes:
//   data-deadline    ISO 8601 date with timezone offset, e.g. "2026-09-01T10:00:00+02:00"
//   data-format      "compact" (inline text) | "digits" (large banner digits)
//   data-prefix      compact only: text rendered before the time, e.g. "PRE ORDER ENDET IN"
//   data-ended-text  shown when the deadline has passed; element hides itself if empty
//
// Markup contract: "digits" mode expects child [data-unit="days|hours|minutes|seconds"]
// spans (server-rendered for a no-JS fallback); "compact" mode expects [data-compact].

class CountdownTimer extends HTMLElement {
  connectedCallback() {
    this.deadline = new Date(this.dataset.deadline || '');

    if (Number.isNaN(this.deadline.getTime())) {
      this.hidden = true;
      return;
    }

    this.tick = this.tick.bind(this);
    this.tick();
    this.interval = setInterval(this.tick, 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  tick() {
    const remaining = this.deadline.getTime() - Date.now();

    if (remaining <= 0) {
      clearInterval(this.interval);
      this.end();
      return;
    }

    const seconds = Math.floor(remaining / 1000) % 60;
    const minutes = Math.floor(remaining / 60000) % 60;
    const hours = Math.floor(remaining / 3600000) % 24;
    const days = Math.floor(remaining / 86400000);

    if (this.dataset.format === 'digits') {
      this.renderDigits({ days, hours, minutes, seconds });
    } else {
      this.renderCompact({ days, hours, minutes, seconds });
    }
  }

  renderDigits({ days, hours, minutes, seconds }) {
    const pad = (value) => String(value).padStart(2, '0');
    const units = {
      days: String(days),
      hours: pad(hours),
      minutes: pad(minutes),
      seconds: pad(seconds),
    };

    for (const [unit, value] of Object.entries(units)) {
      const target = this.querySelector(`[data-unit="${unit}"]`);
      if (target && target.textContent !== value) target.textContent = value;
    }
  }

  renderCompact({ days, hours, minutes, seconds }) {
    const target = this.querySelector('[data-compact]');
    if (!target) return;

    const dayLabel = days === 1 ? 'TAG' : 'TAGEN';
    const time =
      days > 0
        ? `${days} ${dayLabel} ${hours} STD ${minutes} MIN ${seconds} S`
        : `${hours} STD ${minutes} MIN ${seconds} S`;
    const prefix = this.dataset.prefix ? `${this.dataset.prefix} ` : '';

    target.textContent = `${prefix}${time}`;
  }

  end() {
    const endedText = this.dataset.endedText || '';

    if (!endedText) {
      this.hidden = true;
      return;
    }

    this.setAttribute('ended', '');

    if (this.dataset.format === 'digits') {
      const timer = this.querySelector('[data-timer]');
      if (timer) timer.replaceChildren(endedText);
    } else {
      const target = this.querySelector('[data-compact]');
      if (target) target.textContent = endedText;
    }
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
